<template>
  <div class="flex flex-col items-center justify-center min-h-screen w-full overflow-hidden">
    <!-- Login Form -->
     <div class="flex flex-col items-center justify-center mb-16">
    <img :src="logoSrc" alt="Logo" width="150" class="h-[130px] mb-6 mr-1 backlight" />
    <Card class="w-64">
      <template #content>
        <form @submit.prevent="handleLogin" autocomplete="on">
          <div class="mb-6">
            <FloatLabel variant="on">
              <InputText
                id="username"
                name="username"
                v-model="username"
                type="email"
                required
                class="w-full"
                autocomplete="username"
              />
              <label for="username">Account email</label>
            </FloatLabel>
          </div>
          <div class="mb-6">
            <FloatLabel variant="on">
              <InputText
                id="password"
                name="password"
                v-model="password"
                required
                class="w-full"
                type="password"
                autocomplete="current-password"
              />
              <label for="password">Password</label>
            </FloatLabel>
          </div>
          <div class="mb-4 flex items-center gap-2">
            <Checkbox id="rememberMe" v-model="rememberMe" :binary="true" inputId="rememberMe" />
            <label for="rememberMe" class="text-sm cursor-pointer" @click.prevent="rememberMe = !rememberMe">Remember me</label>
          </div>
        <div class="flex flex-column align-items-center justify-content-center">
          <Button label="Login" type="submit" class="submit-button mt-2" :loading="isLoading" severity="primary" />
          <!-- <Button label="Register" text class="mt-2" :loading="isLoading" severity="primary" @click="testCall" /> -->
        </div>
        </form>
        <div class="flex flex-col items-center justify-content-center">
        <Button :icon="themeIcon" class="mt-4  theme-toggle-btn" severity="secondary" text @click="toggleTheme" />
      </div>
      </template>
    </Card>

    <!-- <Button label="Show" @click="show" /> -->
    <p class="login-version footer-text text-xs mt-4" aria-label="Application version">
      Version {{ appVersionLabel }}
    </p>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';

import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Card from 'primevue/card';
import FloatLabel from 'primevue/floatlabel';
import Checkbox from 'primevue/checkbox';

import { useToast } from 'primevue/usetoast';
import api from '@/utils/axios';
import { getDefaultAuthedRoute } from '@/utils/getDefaultAuthedRoute';
import { formatAppVersionLabel } from '@/constants/appMeta';

const isDev =
  (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.DEV) ||
  (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production');

export default {
  components: { Button, InputText, Card, FloatLabel, Checkbox },
  setup() {
    const toast = useToast();
    const store = useStore();
    const router = useRouter();
    const username = ref('');
    const password = ref('');
    const isLoading = ref(false);
    const rememberMe = ref(false);

    const LAST_EMAIL_KEY = 'hr_last_login_email';
    const REMEMBER_ME_KEY = 'hr_remember_me';
    // Passwords are never persisted. Earlier builds stored them, so purge those keys on mount.
    const PURGED_PASSWORD_KEYS = ['hr_remembered_password', 'dcr_remembered_password'];

    const toggleTheme = () => {
      const newTheme = currentTheme.value === 'dark' ? 'light' : 'dark';
      currentTheme.value = newTheme;
      document.documentElement.classList.remove('dark-mode');
      if (newTheme === 'dark') {
        document.documentElement.classList.add('dark-mode');
      }
      store.dispatch('changeTheme', newTheme);
    };

    const persistRememberPreference = () => {
      const email = username.value?.trim() || '';
      if (rememberMe.value && email) {
        localStorage.setItem(REMEMBER_ME_KEY, '1');
        localStorage.setItem(LAST_EMAIL_KEY, email);
        return;
      }
      localStorage.removeItem(REMEMBER_ME_KEY);
      localStorage.removeItem(LAST_EMAIL_KEY);
    };

    const handleLogin = async () => {
      persistRememberPreference();

      try {
        isLoading.value = true;

        if (isDev) {
          console.log('Login attempt:', { email: username.value });
        }

        const response = await api.post('/users/login', {
          email: username.value,
          pass: password.value
        });

        if (isDev) {
          console.log('Login response:', response?.data, response?.status);
        }

        const data = response.data;

        if (data.success) {
          password.value = '';

          await store.dispatch('users/loginUser', data);
          const meOk = await store.dispatch('users/fetchMe');
          if (!meOk) {
            toast.add({
              severity: 'error',
              summary: 'Session Error',
              detail: 'Could not load your profile. Please try again.',
              life: 5000
            });
            return;
          }

          toast.add({
            severity: 'success',
            summary: 'Welcome!',
            detail: 'Login successful',
            life: 3000
          });
          router.push(getDefaultAuthedRoute(store.state.users?.user));
        } else {
          if (isDev) {
            console.log('Login failed:', data?.type, data?.message);
          }

          // Backend rate limit: show server message only (protection is on backend)
          if (data.type === 'rate_limit') {
            toast.add({
              severity: 'error',
              summary: 'Rate Limited',
              detail: data.message,
              life: 5000
            });
            return;
          }

          const errorMessage = data.message || 'Login failed';
          toast.add({
            severity: 'error',
            summary: 'Login Failed',
            detail: errorMessage,
            life: 3000
          });
        }
      } catch (error) {
        if (isDev) {
          console.error('Login error:', error?.message, error?.response?.data);
        }

        const isNetworkError = error.code === 'ERR_NETWORK';
        const errorMessage = isNetworkError
          ? 'Server connection failed. Please check your network connection.'
          : (error.response?.data?.message || 'Login failed. Please try again later.');
        const summary = isNetworkError ? 'Connection Error' : 'Login Failed';

        toast.add({
          severity: 'error',
          summary,
          detail: errorMessage,
          life: 5000
        });
      } finally {
        isLoading.value = false;
      }
    };



    const logoSrc = computed(() => `${process.env.BASE_URL}icons/favicon.svg`);

    const currentTheme = ref(localStorage.getItem('isTheme') || 'dark');

    const themeIcon = computed(() => {
      return currentTheme.value === 'dark' ? 'pi pi-sun' : 'pi pi-moon';
    });

    const appVersionLabel = formatAppVersionLabel();

    const migrateLegacyLoginStorage = () => {
      const legacyMap = [
        ['dcr_last_login_email', LAST_EMAIL_KEY],
        ['dcr_remember_me', REMEMBER_ME_KEY],
      ];
      for (const [legacyKey, newKey] of legacyMap) {
        const value = localStorage.getItem(legacyKey);
        if (value !== null && localStorage.getItem(newKey) === null) {
          localStorage.setItem(newKey, value);
        }
        if (value !== null) {
          localStorage.removeItem(legacyKey);
        }
      }
      PURGED_PASSWORD_KEYS.forEach((key) => localStorage.removeItem(key));
    };

    onMounted(() => {
      migrateLegacyLoginStorage();

      rememberMe.value = localStorage.getItem(REMEMBER_ME_KEY) === '1';
      if (rememberMe.value) {
        const savedEmail = localStorage.getItem(LAST_EMAIL_KEY);
        if (savedEmail) username.value = savedEmail;
      } else {
        localStorage.removeItem(LAST_EMAIL_KEY);
      }

      //fetchClientStats();
      store.dispatch('initializeTheme').then(() => {
        const isTheme = localStorage.getItem('isTheme') || 'dark';
        currentTheme.value = isTheme;
        if (isTheme === 'dark') {
          document.documentElement.classList.add("dark-mode");
        } else {
          document.documentElement.classList.remove("dark-mode");
        }
      });
    });

    return { username, password, rememberMe, isLoading, handleLogin, toggleTheme, logoSrc, themeIcon, appVersionLabel };
  },
};
</script>

<style scoped>
.login-form {
  width: 100%;
  max-width: 400px;
  padding: 20px;
  border: 1px solid #ccc;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  background-color: #fff;
}

.form-group {
  margin-bottom: 20px;
}

.submit-button {
  width: 100%;
}

.logo-title {
  font-size: 1.5em;
  margin-bottom: 20px;
  font-weight: 900;
}

.footer-text {
  color: var(--p-gray-400);
}

.login-version {
  margin-bottom: 0;
  letter-spacing: 0.02em;
}

.dark-mode .footer-text {
  color: var(--p-gray-500);
}

/* FloatLabel стили для лучшего отображения */
:deep(.p-float-label) {
  margin-bottom: 1rem;
}

:deep(.p-float-label label) {
  color: var(--p-text-color-secondary);
  transition: all 0.2s ease-in-out;
}

:deep(.p-float-label input:focus ~ label),
:deep(.p-float-label input.p-filled ~ label) {
  color: var(--p-primary-color);
}

/* Увеличенная иконка темы только на странице логина */
.theme-toggle-btn :deep(.p-button-icon) {
  font-size: 1.5rem !important;
}

.theme-toggle-btn {
  width: 3rem;
  height: 3rem;
}

/* Кнопка логина использует глобальные стили */
</style>
